import { Arc } from '../ui/component/svg-arc';
import { UITooltip } from '../ui/component/tooltip';
import { $path } from '../ui/path';
import { UISVGContent, $svg, UISVG } from '../ui/svg';
import { UIPoint, UISize, UIClassesSpec } from '../ui/types';
import { CHChartElement } from './chart';
import { CHPoint } from './point';

export interface CHPieChartOptions {
    background?: string;
    total?: number;
    hole?: number;
    hole_color?: string;
    stroke?: string;
    labels?: boolean;
    values?: boolean | ((p: CHPoint<string, number>) => string);
}

export class CHPiePlot implements UISVGContent, CHChartElement {
    root: SVGGElement;

    _data: CHPoint<string, number>[];
    _options: CHPieChartOptions;

    _center?: UIPoint;
    _radius?: number;

    _gradient?: string;

    constructor(data: CHPoint<string, number>[], options: CHPieChartOptions = {}) {
        this.root = $svg.G('ch-pie');

        this._data = data;
        this._options = options;
    }

    layout(size: UISize) {
        this._radius = Math.min(size[0], size[1]) / 2 * 0.8;
        this._center = [size[0] / 2, size[1] / 2];
    }

    render(svg: UISVG) {
        const root = this.root;
        root.innerHTML = '';

        if(!this._gradient)
            this._gradient = svg.linearGradient('#244442', '#6cccc6');

        const data = this._data;
        const radius = this._radius!;
        const center = this._center!;

        const options = this._options;

        const total = options.total || data.reduce((a, d) => a + d.y, 0);

        const r2 = radius * 0.8;
        const r1 = (options.hole || 0) * r2
        const r3 = radius * 0.9;
        const r4 = radius * 0.85;
        const r5 = radius * 0.6;

        if(options.hole_color) {
            $path.start();
            $path.arc(center[0], center[1], r1 + 1, 0, 2 * Math.PI, false);
            const hole = $path.toElem();
            root.appendChild(hole);
        }

        $path.start();
        $path.arc(center[0], center[1], r1, 0, 2 * Math.PI, false);
        $path.arc(center[0], center[1], r2, 2 * Math.PI, 0, true);
        $path.close();
        const back = $path.toElem();
        back.style.fill = '#eee';
        root.appendChild(back);

        if(total <= 0) return;
        const angle_factor = 2 * Math.PI / total;
        let angle = -Math.PI / 2;

        // const nice_colors = ['#c4f080','#3cb29a','#266d98','#422b72','#2D4059','#EA5455','#F07B3F','#FFD460','#C94E4E','#713045','#430D27','#25161B','#453953',
        //                    '#975A5E','#F3CBA5','#57385C','#A75265','#EC7263','#FEBE7E'];

        const nice_colors = ['#ffc266', '#d284bd', '#8784db', '#ff7b65', '#caeefc', '#9adbad', '#fff1b2', '#ffe0b2', '#ffbeb2', '#b1afdb'];

        const small_angle = Math.PI / 90;

        for(let i = 0; i < data.length; i++) {
            const d = data[i];
            const start = angle;
            angle += angle_factor * d.y;
            let slice: Arc;

            if(options.stroke) {
                slice = new Arc(center, r1, r2, start, angle);
                slice.root.style.stroke = options.stroke;
            } else {
                slice = new Arc(center, r1, r2, i == 0 ? start - small_angle : start, i != data.length - 1 ? angle + small_angle : angle);
            }

            if(options.labels) {
                let a = (start + angle) / 2;
                let point = [center[0] + Math.cos(a) * r3, center[1] + Math.sin(a) * r3];
                let text_classes: UIClassesSpec | undefined;

                let poly: number[] = [center[0] + Math.cos(a) * r4, center[1] + Math.sin(a) * r4, point[0], point[1]];

                if(a < Math.PI / 2) {
                    point[0] = center[0] + radius * 0.9;
                    poly.push(center[0] + radius * 0.85, point[1])
                    if(poly[4] < poly[2] + 5)
                        poly = [];
                } else {
                    point[0] = center[0] - radius * 0.9;
                    poly.push(center[0] - radius * 0.85, point[1])
                    if(poly[4] > poly[2] - 5)
                        poly = [];

                    text_classes = 'm-end';
                }

                if(poly.length)
                    root.appendChild($svg.POLYLINE(poly));

                root.appendChild($svg.TEXT(d.x, text_classes, point[0], point[1], undefined, '0.35em'));
            }

            slice.root.style.fill = nice_colors[i % nice_colors.length];
            root.appendChild(slice.root);

            if(options.values) {
                const a = (start + angle) / 2;
                const point = [center[0] + Math.cos(a) * r5, center[1] + Math.sin(a) * r5];
                const text = typeof options.values == 'function' ? options.values(d) : d.y + '';
                root.appendChild($svg.TEXT(text, 'm-center', point[0], point[1], undefined, '0.35em'));
            }
        }

        svg.add(this.root);
    }

    onMouse(_point: UIPoint | undefined, _tooltip: UITooltip) {

    }
}
